import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import Select, { OnChangeValue, StylesConfig } from 'react-select'
import styles from './MultiScaleComponent.module.scss';
import Radio from '../../../../components/radio/Radio';
import TextSpeech from '@src/components/text-speech/TextSpeech';

type Props = {
  value?: Record<string, string>;
  options: string[];
  onChange: (id: string, value: string) => void;
  statements: {
    id: string;
    value: string;
  }[];
  hasError: boolean;
  errorMessage?: string;
  needSpeak?: boolean;
  isAU?: boolean;
};

const selectStyles: StylesConfig<any, false> = {
  control: (provided) => ({
    ...provided,
    margin: 5,
    color: '#FFE4CF',
  }),
  menu: (provided) => ({ ...provided, borderWidth: 1, borderColor: '#000', position: 'relative' }),
  singleValue: (provided) => ({ ...provided, fontWeight: 500 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#FFB073' : state.isFocused ? '#FFE4CF' : 'transparent',
    borderWidth: 2, borderColor: '#000',
  }),
};

function MultiScaleComponent(props: Props) {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { options, statements, onChange, value, needSpeak, isAU=false } = props;
  const [focusedIndex, setFocus] = useState<number>(0);

  const handleChange = (id: string, value: string) => () => {
    onChange(id, value);
  };
  const onSelectChange = (selectedData: OnChangeValue<{label: string, value: string}, false>, rowIndex: number) => {
    if (selectedData) {
      const [id, v] = selectedData.value.split('__')
      if (id && v) {
        onChange(id, v);
        setFocus(rowIndex + 1);
      }
    }
  };

  if (isMobile) {
    return (
      <div className={styles.wrapper}>
        {statements.map((s, rowIndex) => {
          return (
            <div key={`${s.id}-${rowIndex}`}>
              <div className={styles.rowTitle}>{s.value}
                {needSpeak && <TextSpeech text={s.value ?? ''} isAU={isAU} />}
              </div>
              <Select
                options={options.map((v) => ({value: `${s.id}__${v}`, label: v}))}
                backspaceRemovesValue={false}
                components={{ IndicatorSeparator: null }}
                hideSelectedOptions={false}
                isClearable={false}
                isSearchable={false}
                onChange={(date) => onSelectChange(date, rowIndex)}
                placeholder={'Please select...'}
                styles={selectStyles}
                defaultValue={`${s.id}__${value?.[s.id] || ''}`}
                theme={(theme) => ({
                  ...theme,
                  border: 0,
                  borderWidth: 0,
                  colors: {
                    ...theme.colors,
                    primary25: '#FFE4CF',
                    primary: '#FFB073',
                  },
                })}
                menuIsOpen={focusedIndex === rowIndex}
                onFocus={() => setFocus(rowIndex)}
              />
            </div>
          );
        })}
        {props.errorMessage ? <div className={styles.errorText}>{props.errorMessage}</div> : null}
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentRow}>
        <div className={styles.title}>&nbsp;</div>
        {options.map((o, index) => {
          return (
            <div className={styles.headerItem} key={index}>
              <p className={styles.headerItemContent}>{o}</p>
            </div>
          );
        })}
      </div>
      {statements.map((s, rowIndex) => {
        return (
          <div className={styles.contentRow} key={rowIndex}>
            <div className={styles.rowTitle}>{s.value}
              {needSpeak && <TextSpeech text={s.value ?? ''} isAU={isAU} />}
            </div>
            {options.map((o, colIndex) => {
              const key = `${rowIndex}-${colIndex}`;
              const checked = value?.[s.id] === o;
              return (
                <div className={styles.contentItem} key={colIndex}>
                  <Radio id={key} checked={checked} onChange={handleChange(s.id, o)} name={key}/>
                </div>
              );
            })}
          </div>
        );
      })}
      {props.errorMessage ? <div className={styles.errorText}>{props.errorMessage}</div> : null}
    </div>
  );
}

export default MultiScaleComponent;
